import * as TID from '@atcute/tid';

import { DEFAULT_MODERATION_LABELER } from '~/api/globals/defaults';
import type { ModerationOptions } from '~/api/moderation';
import type { LanguagePreferences, TranslationPreferences, VerificationPreferences } from '~/api/types';

import { createReactiveLocalStorage } from '~/utils/storage';

import { desktopEvents } from './events';
import { type DeckConfig, type PaneConfig, PaneSize, SpecificPaneSize } from './panes';

export interface ModerationPreferences extends Omit<ModerationOptions, '_filtersCache'> {
	updatedAt: number;
}

export interface PreferencesSchema {
	$version: 17;
	/** Onboarding mode */
	onboarding: boolean;
	/** Deck configuration */
	decks: DeckConfig[];
	/** UI configuration */
	ui: {
		/** Application theme */
		theme: 'auto' | 'dark' | 'light';
		/** Font family — id from FONT_OPTIONS in fonts.ts */
		font: string;
		/** Default pane size */
		defaultPaneSize: PaneSize;
		/** Show grid UI for profile media */
		profileMediaGrid: boolean;
		/** Show thread replies in a threaded view */
		threadedReplies: boolean;
		/** Who can reply to your posts */
		defaultReplyGate: 'e' | 'm' | 'f';
	};
	/** Accessibility configuration */
	a11y: {
		/** Warn if the media being posted contains no alt text */
		warnNoMediaAlt: boolean;
	};
	/** Content moderation */
	moderation: ModerationPreferences;
	/** Language configuration */
	language: LanguagePreferences;
	/** Translation configuration */
	translation: TranslationPreferences;
	/** Verification badge preferences */
	verification: VerificationPreferences;
}

const PREF_KEY = 'rantai_prefs';

export const preferences = createReactiveLocalStorage<PreferencesSchema>(PREF_KEY, (version, prev) => {
	if (version === 0) {
		const object: PreferencesSchema = {
			$version: 17,
			onboarding: true,
			decks: [],
			ui: {
				theme: 'auto',
				font: 'system',
				defaultPaneSize: PaneSize.MEDIUM,
				profileMediaGrid: true,
				threadedReplies: false,
				defaultReplyGate: 'e',
			},
			a11y: {
				warnNoMediaAlt: true,
			},
			moderation: {
				updatedAt: 0,
				labels: {},
				services: [
					{
						did: DEFAULT_MODERATION_LABELER,
						redact: true,
						profile: { handle: 'moderation.bsky.app' },
						prefs: {},
						vals: [],
						defs: {},
					},
				],
				keywords: [],
				hideReposts: [],
				tempMutes: {},
			},
			language: {
				languages: [],
				useSystemLanguages: true,
				allowUnspecified: true,
				defaultPostLanguage: 'system',
			},
			translation: {
				to: 'system',
				exclusions: [],
				provider: 'google',
				autoTranslate: false,
				openRouterApiKey: '',
				openRouterModel: '',
				openRouterReasoning: false,
			},
			verification: {
				hideBadges: false,
				trustedVerifiers: [],
			},
		};

		return object;
	}

	if (version < 10) {
		const _next = prev as PreferencesSchema;

		delete prev.rev;

		_next.moderation = {
			updatedAt: 0,
			labels: {},
			services: [
				{
					did: DEFAULT_MODERATION_LABELER,
					redact: true,
					profile: { handle: 'moderation.bsky.app' },
					prefs: {},
					vals: [],
					defs: {},
				},
			],
			keywords: prev.moderation.keywords,
			hideReposts: prev.filters.hideReposts,
			tempMutes: prev.filters.tempMutes,
		};
	}

	if (version < 11) {
		const _next = prev as PreferencesSchema;

		_next.ui.defaultReplyGate = 'e';
	}

	if (version < 12) {
		const _next = prev as PreferencesSchema;

		for (const deck of _next.decks) {
			for (const pane of deck.panes) {
				if (pane.type === 'search') {
					pane.sort = 'latest';
				}
			}
		}
	}

	if (version < 13) {
		const _next = prev as PreferencesSchema;

		for (const deck of _next.decks) {
			for (const pane of deck.panes) {
				if (pane.type === 'notifications') {
					pane.mask = 0;
				}
			}
		}

		_next.$version = 13 as any;
	}

	if (version < 14) {
		const _next = prev as PreferencesSchema;

		_next.verification = {
			hideBadges: false,
			trustedVerifiers: [],
		};

		_next.$version = 14 as any;
	}

	if (version < 15) {
		const _next = prev as PreferencesSchema;

		_next.ui.font = 'system';
		_next.$version = 15 as any;
	}

	if (version < 16) {
		const _next = prev as PreferencesSchema;

		// Inline OpenRouter translation lands disabled by default; existing users
		// keep the built-in Google engine and manual (click-to-translate) behaviour.
		_next.translation = {
			to: _next.translation?.to ?? 'system',
			exclusions: _next.translation?.exclusions ?? [],
			provider: 'google',
			autoTranslate: false,
			openRouterApiKey: '',
			openRouterModel: '',
			openRouterReasoning: false,
		};

		_next.$version = 16 as any;
	}

	if (version < 17) {
		const _next = prev as PreferencesSchema;

		// Merge the old separate "auto-refresh" and "realtime" toggles into a single
		// realtime toggle plus a "pin to top" option. The legacy auto-refresh reset
		// the column and jumped to the top, so it maps to realtime + pinToTop; the
		// legacy realtime streamed in while preserving scroll, so pinToTop stays off.
		for (const deck of _next.decks) {
			for (const pane of deck.panes) {
				const legacy = pane as { autoRefresh?: boolean; realtime?: boolean; pinToTop?: boolean };

				if (legacy.autoRefresh && !legacy.realtime) {
					legacy.realtime = true;
					legacy.pinToTop = true;
				}

				delete legacy.autoRefresh;
			}
		}

		_next.$version = 17;
	}

	return prev;
});

export const addPane = <T extends PaneConfig>(
	deck: DeckConfig,
	partial: Omit<T, 'id' | 'size' | 'title'>,
	index?: number,
) => {
	// @ts-expect-error
	const pane: PaneConfig = {
		...partial,
		id: TID.now(),
		size: SpecificPaneSize.INHERIT,
		title: null,
	};

	if (index !== undefined) {
		deck.panes.splice(index, 0, pane);
	} else {
		deck.panes.push(pane);
	}

	desktopEvents.emit(`focuspane:${pane.id}`);
};

export const resolvePaneSize = (size: SpecificPaneSize): PaneSize => {
	if (size === SpecificPaneSize.INHERIT) {
		return preferences.ui.defaultPaneSize;
	}

	return size as any as PaneSize;
};
